/**
 * Nexus-requirement waivers.
 *
 * The nexus pillar (medical opinion linking condition → service) is the
 * single hardest piece of evidence to obtain. Two common scenarios make it
 * effectively unnecessary, and the VA processes them accordingly:
 *
 *   1. BDD / Pre-discharge claims — the veteran is filing while still in
 *      service, so direct service connection is inherent. No external nexus
 *      letter is required (the in-service medical record IS the link).
 *
 *   2. FDC (Fully Developed Claim) filed within ONE YEAR of discharge —
 *      under 38 CFR § 3.307, chronic diseases shown during the first
 *      post-service year are presumed service-connected. With current STRs
 *      already in hand at FDC filing, the "first year" presumption replaces
 *      the need for an external nexus opinion.
 *
 * This module is the single source of truth for the waiver decision. The
 * Claim Tracker UI, evidence-gap computation, Master PDF, and readiness
 * snapshot all defer to `nexusRequirementWaived(workspace, master526ez)`.
 *
 * The waiver is reversible: change the workspace claim type or push the
 * discharge date past 365 days and the pillar goes back to "need".
 */
import { CLAIM_TYPES } from "./claimEnums";
import { findClaimType } from "./claimTypesCatalog";

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Resolve the master526ez.claimType field to its canonical catalog id.
 * The form historically persists the human-readable LABEL (e.g.
 * "Fully Developed Claim (FDC)") rather than the id ("fdc"), and we don't
 * want to migrate stored docs, so we normalise here at the read boundary.
 */
function resolveFormClaimTypeId(master526ez) {
  const raw = (master526ez?.claimType || "").trim();
  if (!raw) return "";
  const match = findClaimType(raw);
  return match ? match.id : raw.toLowerCase();
}

/**
 * Is this workspace a BDD / pre-discharge claim? We accept either the
 * workspace-level claimType (canonical string from CLAIM_TYPES) or the
 * Master 526EZ form-level claimType id ("bdd"). Either match is enough —
 * the user might set one without the other in the early-draft phase.
 *
 * For defensive robustness, we also normalise the workspace label through
 * findClaimType() so the longer catalog label ("Benefits Delivery at
 * Discharge (BDD)") resolves correctly if a workspace was ever populated
 * from that catalog instead of CLAIM_TYPE_OPTIONS.
 */
function isBddClaim(workspace, master526ez) {
  const wsType = (workspace?.claimType || "").trim();
  if (wsType === CLAIM_TYPES.bdd) return true;
  if (wsType) {
    const match = findClaimType(wsType);
    if (match && match.id === "bdd") return true;
  }
  return resolveFormClaimTypeId(master526ez) === "bdd";
}

/** Is this an FDC (Fully Developed Claim)? Only the form-level type can be FDC. */
function isFdcClaim(master526ez) {
  return resolveFormClaimTypeId(master526ez) === "fdc";
}

/**
 * Returns the number of days BETWEEN the discharge date and today (or
 * null if there's no parseable serviceEnd on file). Negative numbers mean
 * the discharge is in the future (BDD territory). Positive = post-discharge.
 */
export function daysSinceDischarge(master526ez) {
  const raw = master526ez?.serviceEnd;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Returns {waived: boolean, reason: string|null} describing whether the
 * nexus pillar requirement is auto-satisfied for this workspace.
 *
 * Callers should treat `waived: true` exactly like "evidence present" —
 * the pillar is green, no warning, no PDF "nexus gap" callout.
 */
export function nexusWaiver(workspace, master526ez) {
  if (isBddClaim(workspace, master526ez)) {
    return {
      waived: true,
      reason: "BDD / Pre-discharge claim — direct service connection is inherent (filed while still in service). The in-service medical record IS the nexus.",
      shortReason: "BDD — nexus inherent",
    };
  }
  if (isFdcClaim(master526ez)) {
    const days = daysSinceDischarge(master526ez);
    if (days !== null && days >= 0 && days <= 365) {
      return {
        waived: true,
        reason: `FDC filed within 1 year of discharge (${days} day${days === 1 ? "" : "s"} post-separation). 38 CFR § 3.307 first-year presumption applies for chronic conditions — no external nexus opinion required.`,
        shortReason: "FDC within 1 yr of discharge",
      };
    }
  }
  return { waived: false, reason: null, shortReason: null };
}

/** Convenience: just the boolean. */
export function nexusRequirementWaived(workspace, master526ez) {
  return nexusWaiver(workspace, master526ez).waived;
}
